import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { rupee, effectivePrice } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/StarRating";
import { GroceryCard } from "@/components/GroceryCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShoppingBasket, Minus, Plus, ChevronLeft, Loader2, MessageSquare } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [similar, setSimilar] = useState([]);

  usePageMeta(product ? product.name : "Product", product?.description);

  const loadReviews = () => api.get(`/products/${id}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    setQty(1);
    setSimilar([]);
    api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
    loadReviews();
    api.get(`/products/${id}/similar`).then(({ data }) => setSimilar(data)).catch(() => {});
    api.post(`/products/${id}/click`).catch(() => {});
    window.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitReview = async () => {
    if (!myRating) {
      toast.error("Please select a star rating");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/products/${id}/reviews`, { rating: myRating, comment: myComment });
      toast.success("Thanks for your review!");
      setMyComment("");
      setMyRating(0);
      await loadReviews();
      const { data } = await api.get(`/products/${id}`);
      setProduct(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  if (notFound || !product)
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <ShoppingBasket className="mx-auto h-12 w-12 text-ink-muted" />
        <h1 className="mt-4 font-heading text-2xl text-ink">Product not found</h1>
        <Link to="/products"><Button className="mt-6 rounded-full bg-brand text-ink">Back to products</Button></Link>
      </div>
    );

  const images = (product.images && product.images.length) ? product.images : (product.image_id ? [product.image_id] : []);
  const mainImg = images.length ? fileUrl(images[Math.min(activeImg, images.length - 1)]) : null;
  const soldOut = product.stock <= 0;
  const hasDiscount = product.discount_price && product.discount_price > 0;

  const add = () => { addItem(product, qty); toast.success(`${qty} × ${product.name} added to cart`); };
  const buyNow = () => { addItem(product, qty); navigate("/cart"); };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-1 text-sm text-ink-soft hover:text-brand" data-testid="back-btn">
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
          <div className="overflow-hidden rounded-[2rem] border border-border/60 bg-cream-alt shadow-ambient">
            {mainImg ? (
              <img src={mainImg} alt={product.name} className="h-full max-h-[520px] w-full object-cover" data-testid="detail-main-image" />
            ) : (
              <div className="flex aspect-square items-center justify-center text-ink-muted"><ShoppingBasket className="h-16 w-16" /></div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
              {images.map((id, idx) => (
                <button
                  key={id}
                  onClick={() => setActiveImg(idx)}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${idx === activeImg ? "border-brand" : "border-transparent hover:border-border"}`}
                  data-testid={`detail-thumb-${idx}`}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img src={fileUrl(id)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        <div>
          <h1 className="font-heading text-3xl font-bold text-ink sm:text-4xl">{product.name}</h1>
          {product.seller_name && (
            <p className="mt-1 text-sm text-ink-soft" data-testid="product-seller">Sold by <span className="font-semibold text-ink">{product.seller_name}</span></p>
          )}

          {product.rating_count > 0 && (
            <div className="mt-2 flex items-center gap-2" data-testid="detail-rating">
              <StarRating value={product.rating_avg} />
              <span className="text-sm text-ink-soft">{product.rating_avg} · {product.rating_count} review{product.rating_count > 1 ? "s" : ""}</span>
            </div>
          )}

          <div className="mt-4 flex items-baseline gap-3">
            <span className="text-3xl font-bold text-brand">{rupee(effectivePrice(product))}</span>
            {hasDiscount && <span className="text-lg text-ink-muted line-through">{rupee(product.price)}</span>}
            <span className="text-sm text-ink-muted">/ {product.unit}</span>
          </div>

          <p className="mt-5 leading-relaxed text-ink-soft">
            {product.description || "Quality product, carefully sourced and delivered to your doorstep."}
          </p>

          <div className="mt-6">
            {soldOut ? (
              <span className="inline-block rounded-full bg-destructive/10 px-4 py-1.5 text-sm font-semibold text-destructive" data-testid="sold-out-badge">Sold Out</span>
            ) : (
              <span className="inline-block rounded-full bg-sage/10 px-4 py-1.5 text-sm font-semibold text-sage" data-testid="in-stock-badge">Stock left · {product.stock}</span>
            )}
          </div>

          {!soldOut && (
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center rounded-full border border-border">
                <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease"><Minus className="h-4 w-4" /></Button>
                <span className="w-10 text-center font-semibold text-ink" data-testid="qty-value">{qty}</span>
                <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setQty((q) => Math.min(product.stock, q + 1))} data-testid="qty-increase"><Plus className="h-4 w-4" /></Button>
              </div>
              <Button onClick={add} data-testid="detail-add-to-cart" className="rounded-full bg-brand px-8 font-semibold text-ink hover:bg-brand-hover">Add to Cart</Button>
              <Button onClick={buyNow} data-testid="buy-now" className="rounded-full bg-navy px-8 font-semibold text-white hover:bg-navy-hover">Buy Now</Button>
            </div>
          )}
        </div>
      </div>

      {/* Similar products */}
      {similar.length > 0 && (
        <section className="mt-16 border-t border-border pt-10" data-testid="similar-products">
          <h2 className="font-heading text-2xl font-bold text-ink">Similar products</h2>
          <p className="mt-1 text-sm text-ink-soft">More from this category you might like</p>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {similar.slice(0, 8).map((p) => <GroceryCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="mt-16 border-t border-border pt-10">
        <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-ink">
          <MessageSquare className="h-5 w-5 text-brand" /> Ratings & Reviews
        </h2>

        {/* Write a review */}
        <div className="mt-6 rounded-2xl border border-border/60 bg-white p-6 shadow-ambient">
          {user && user.role === "customer" ? (
            <div data-testid="review-form">
              <Label className="text-ink-soft">Your rating</Label>
              <div className="mt-2">
                <StarRating value={myRating} interactive onChange={setMyRating} size="h-7 w-7" />
              </div>
              <Textarea
                value={myComment}
                onChange={(e) => setMyComment(e.target.value)}
                placeholder="Share your experience with this product..."
                className="mt-4 rounded-xl"
                rows={3}
                data-testid="review-comment"
              />
              <Button onClick={submitReview} disabled={submitting} className="mt-4 rounded-full bg-brand font-semibold text-ink hover:bg-brand-hover" data-testid="submit-review">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Review"}
              </Button>
            </div>
          ) : user && user.role === "admin" ? (
            <p className="text-sm text-ink-muted">Admins can view customer reviews here. Customers can submit ratings from their accounts.</p>
          ) : (
            <p className="text-sm text-ink-soft">
              <Link to="/login" className="font-semibold text-brand hover:underline">Log in</Link> to leave a rating and review.
            </p>
          )}
        </div>

        {/* List */}
        <div className="mt-6 space-y-4">
          {reviews.length === 0 ? (
            <p className="text-ink-muted" data-testid="no-reviews">No reviews yet. Be the first to review!</p>
          ) : (
            reviews.map((r) => (
              <div key={r.id} className="rounded-2xl border border-border/60 bg-white p-5 shadow-ambient" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/15 font-semibold text-ink">{r.customer_name?.[0]?.toUpperCase() || "C"}</span>
                    <div>
                      <p className="font-semibold text-ink">{r.customer_name}</p>
                      <StarRating value={r.rating} size="h-3.5 w-3.5" />
                    </div>
                  </div>
                  <span className="text-xs text-ink-muted">{new Date(r.created_at).toLocaleDateString("en-IN")}</span>
                </div>
                {r.comment && <p className="mt-3 text-ink-soft">{r.comment}</p>}
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
